import { createLivePreviewRequestHandler } from "src/utils/livePreviewHandler";

const fetchHighHandDoc = createLivePreviewRequestHandler(
  ({ serverURL, apiPath, docId }) =>
    `${serverURL}${apiPath}/high-hand-promotions/${docId}?draft=true&depth=2`,
);

/**
 * @param {unknown} ref
 * @param {string} serverURL
 * @param {string} apiPath
 */
async function hydrateMediaRef(ref, serverURL, apiPath) {
  if (
    ref &&
    typeof ref === "object" &&
    !Array.isArray(ref) &&
    (typeof ref.url === "string" || typeof ref.filename === "string")
  ) {
    return ref;
  }

  if (typeof ref === "number" || (typeof ref === "string" && /^\d+$/.test(ref))) {
    try {
      const mediaResponse = await fetch(
        `${serverURL}${apiPath}/media/${ref}?depth=0`,
      );
      if (mediaResponse.ok) return mediaResponse.json();
    } catch {
      // Keep original ref if hydration fails.
    }
  }

  return ref;
}

/**
 * Localized uploads come back from live-preview populate as `{ en: id, fr: id }`.
 * Resolve each locale to a media doc so the mapper can build CDN paths.
 *
 * @param {unknown} value
 * @param {string} serverURL
 * @param {string} apiPath
 */
async function hydrateLocalizedMedia(value, serverURL, apiPath) {
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    ("en" in value || "fr" in value) &&
    !value.url &&
    !value.filename
  ) {
    const out = { ...value };
    for (const key of ["en", "fr"]) {
      if (key in out) {
        out[key] = await hydrateMediaRef(out[key], serverURL, apiPath);
      }
    }
    return out;
  }

  return hydrateMediaRef(value, serverURL, apiPath);
}

/**
 * Hydrate upload IDs / `{en,fr}` maps on layout blocks. Live preview often
 * leaves localized uploads unresolved; without this, titan/NPD card images
 * never reach the 2-column grid (hero was the only block hydrated before).
 *
 * @param {unknown[]} blocks
 * @param {string} serverURL
 * @param {string} apiPath
 */
async function hydrateLayoutMedia(blocks, serverURL, apiPath) {
  if (!Array.isArray(blocks)) return;
  for (const block of blocks) {
    if (!block || typeof block !== "object") continue;
    if (block.image != null) {
      block.image = await hydrateLocalizedMedia(block.image, serverURL, apiPath);
    }
    if (block.blockType === "hero" && block.bgImage != null) {
      block.bgImage = await hydrateMediaRef(block.bgImage, serverURL, apiPath);
    }
  }
}

/**
 * Live preview request handler for `high-hand-promotions` (drafts enabled).
 * Hydrates hero + titan/NPD media IDs that Payload leaves unresolved on
 * localized uploads.
 *
 * @param {import('@payloadcms/live-preview').CollectionPopulationRequestHandlerArgs} args
 */
export async function highHandLivePreviewRequestHandler(args) {
  const { apiPath, serverURL } = args;
  const response = await fetchHighHandDoc(args);

  if (!response.ok) return response;

  let doc;
  try {
    doc = await response.json();
  } catch {
    return response;
  }

  if (doc && typeof doc === "object") {
    await hydrateLayoutMedia(doc.layout, serverURL, apiPath);
    if (Array.isArray(doc.tabs)) {
      for (const tab of doc.tabs) {
        if (tab && typeof tab === "object") {
          await hydrateLayoutMedia(tab.layout, serverURL, apiPath);
        }
      }
    }
  }

  return new Response(JSON.stringify(doc), {
    status: 200,
    headers: { "Content-Type": "application/json" },
  });
}
